import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Chip,
  Divider,
  Modal,
  Paper,
  Snackbar,
  SnackbarOrigin,
  TextField,
} from '@mui/material';
import {CloseIcon, SelectPopupIcon, SuccessNotification} from 'assets/icons';
import {Controller, useForm} from 'react-hook-form';
import React, {FC, useState} from 'react';
import {cancelButton, createButtonModal} from 'styles/MUIStyles/buttons';
import {chipStyles, searchAndSelect} from 'styles/MUIStyles/common';
import {
  snackbarAlertContent,
  snackbarSuccessAlert,
} from '../../../styles/MUIStyles/snackbars';

import ButtonWithProgress from 'components/ButtonWithProgress/ButtonWithProgress';
import {ErrorMessage} from '@hookform/error-message';
import {IGetCompaniesListItem} from 'store/models/companies';
import {modal} from 'styles/MUIStyles/modal';
import styles from './CompanyOwnersCreate.module.scss';
import {useCreateCompanyOwnerMutation} from 'store/slices/companyOwners';
import {useGetCompaniesListQuery} from 'store/slices/companies';
import {useUpdateEffect} from '../../../hooks';

interface Props {
  isCreateCompanyOwnersModalOpen: boolean;
  setIsCreateCompanyOwnersModalOpen: (isOpen: boolean) => void;
  search: string;
  pageNumber: number;
  pageSize: number;
  sortConfig: {column: string | null; direction: string | null};
  status: {label: string; value: boolean; queryName: string};
}

interface IFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  additionalInformation: string;
  companies: IGetCompaniesListItem[];
}

interface State extends SnackbarOrigin {
  open: boolean;
}

const CompanyOwnersCreate: FC<Props> = (props) => {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    control,
    formState: {errors},
  } = useForm<IFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      additionalInformation: '',
      companies: [],
    },
  });

  const {data: companiesList} = useGetCompaniesListQuery({});

  const [
    createCompanyOwner,
    {
      isLoading: isCreatingCompanyOwnerLoading,
      isSuccess: isCreatingCompanyOwnerSuccess,
      error: createCompanyOwnerError,
    },
  ] = useCreateCompanyOwnerMutation();

  const [companyOwnerEmail, setCompanyOwnerEmail] = useState<string | null>(
    null,
  );
  const [snackbar, setSnackbar] = useState<State>({
    open: false,
    vertical: 'top',
    horizontal: 'center',
  });
  const {vertical, horizontal, open} = snackbar;

  const onSubmit = async (companyOwnerData: IFormData) => {
    const response = createCompanyOwner({
      ...companyOwnerData,
      firstName: companyOwnerData.firstName.trim(),
      lastName: companyOwnerData.lastName.trim(),
      email: companyOwnerData.email.trim(),
      phoneNumber: companyOwnerData.phoneNumber.trim(),
      additionalInformation: companyOwnerData.additionalInformation.trim(),
      companies: companyOwnerData.companies.map((c) => c.id),
    });

    if (response) {
      if (!('error' in response)) {
        setCompanyOwnerEmail(companyOwnerData.email);
      }
    }
  };

  const clearDataAndErrors = () => {
    clearErrors();
    reset();
    props.setIsCreateCompanyOwnersModalOpen(false);
  };

  const handleSnackbarClose = () => {
    setCompanyOwnerEmail(null);
    setSnackbar({...snackbar, open: false});
  };

  useUpdateEffect(() => {
    if (isCreatingCompanyOwnerSuccess) {
      setSnackbar({vertical: 'top', horizontal: 'center', open: true});
      props.setIsCreateCompanyOwnersModalOpen(false);
      reset();
    }
  }, [isCreatingCompanyOwnerSuccess]);

  useUpdateEffect(() => {
    if (createCompanyOwnerError) {
      if ('data' in createCompanyOwnerError) {
        // backend wraps validation errors in an array: [{ errors: {...} }]
        const data = createCompanyOwnerError.data as any;
        const errors = Array.isArray(data) ? data[0]?.errors : data?.errors;
        if (errors?.email?.[0] || errors?.Email?.[0]) {
          setError('email', {
            type: 'custom',
            message: errors?.email?.[0] || errors?.Email?.[0],
          });
        }

        if (errors?.firstName?.[0] || errors?.FirstName?.[0]) {
          setError('firstName', {
            type: 'custom',
            message: errors?.firstName?.[0] || errors?.FirstName?.[0],
          });
        }

        if (errors?.lastName?.[0] || errors?.LastName?.[0]) {
          setError('lastName', {
            type: 'custom',
            message: errors?.lastName?.[0] || errors?.LastName?.[0],
          });
        }

        if (errors?.phoneNumber?.[0] || errors?.PhoneNumber?.[0]) {
          setError('phoneNumber', {
            type: 'custom',
            message: errors?.phoneNumber?.[0] || errors?.PhoneNumber?.[0],
          });
        }
        if (errors?.companies?.[0] || errors?.Companies?.[0]) {
          setError('companies', {
            type: 'custom',
            message: errors?.companies?.[0] || errors?.Companies?.[0],
          });
        }
      }
    }
  }, [createCompanyOwnerError, setError]);

  return (
    <div>
      <Snackbar
        anchorOrigin={{vertical, horizontal}}
        autoHideDuration={4000}
        open={open}
        onClose={handleSnackbarClose}
        key={vertical + horizontal}
      >
        <Alert icon={false} sx={snackbarSuccessAlert}>
          <Box sx={snackbarAlertContent}>
            <SuccessNotification />
            Password sent to {companyOwnerEmail}
            <span onClick={handleSnackbarClose}>
              <CloseIcon />
            </span>
          </Box>
        </Alert>
      </Snackbar>
      <Backdrop
        sx={{color: 'var(--text-on-primary)', zIndex: (theme) => theme.zIndex.drawer + 1}}
        open={props.isCreateCompanyOwnersModalOpen}
      >
        <Modal
          open={props.isCreateCompanyOwnersModalOpen}
          onClose={clearDataAndErrors}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={modal}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px',
                }}
              >
                <h2 className={styles.companyOwnersCreateTitle}>
                  Create company owner
                </h2>
                <div className={styles.closeIcon} onClick={clearDataAndErrors}>
                  <CloseIcon />
                </div>
              </Box>
              <div className={styles.firstNameInputContainer}>
                <label htmlFor="firstName">First Name</label>
                <input
                  {...register('firstName', {
                    required: 'This field is required',
                  })}
                  type="text"
                  id="firstName"
                  autoComplete="off"
                  className={`${
                    errors?.firstName ? 'input inputError' : 'input'
                  }`}
                  placeholder="Enter First Name"
                />
                <ErrorMessage
                  name="firstName"
                  errors={errors}
                  render={({message}) => (
                    <span className={styles.inputErrorMessage}>{message}</span>
                  )}
                />
              </div>

              <div className={styles.lastNameInputContainer}>
                <label htmlFor="lastName">Last Name</label>
                <input
                  {...register('lastName', {
                    required: 'This field is required',
                  })}
                  type="text"
                  id="lastName"
                  autoComplete="off"
                  className={`${
                    errors?.lastName ? 'input inputError' : 'input'
                  }`}
                  placeholder="Enter Last Name"
                />
                <ErrorMessage
                  name="lastName"
                  errors={errors}
                  render={({message}) => (
                    <span className={styles.inputErrorMessage}>{message}</span>
                  )}
                />
              </div>

              <div className={styles.emailInputContainer}>
                <label htmlFor="email">Email</label>
                <input
                  {...register('email', {
                    required: 'This field is required',
                    pattern: {
                      value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                      message: 'Invalid email address',
                    },
                  })}
                  type="text"
                  id="email"
                  autoComplete="off"
                  className={`${errors?.email ? 'input inputError' : 'input'}`}
                  placeholder="Enter Email"
                />
                <ErrorMessage
                  name="email"
                  errors={errors}
                  render={({message}) => (
                    <span className={styles.inputErrorMessage}>{message}</span>
                  )}
                />
              </div>

              <div className={styles.phoneNumberInputContainer}>
                <label htmlFor="phoneNumber">Phone number</label>
                <input
                  {...register('phoneNumber', {
                    required: 'This field is required',
                  })}
                  type="text"
                  id="phoneNumber"
                  autoComplete="off"
                  className={`${
                    errors?.phoneNumber ? 'input inputError' : 'input'
                  }`}
                  placeholder="Enter phone number"
                />
                <ErrorMessage
                  name="phoneNumber"
                  errors={errors}
                  render={({message}) => (
                    <span className={styles.inputErrorMessage}>{message}</span>
                  )}
                />
              </div>

              <div className={styles.phoneNumberInputContainer}>
                <label htmlFor="companies">Companies</label>
                <Controller
                  name="companies"
                  control={control}
                  rules={{required: 'This field is required'}}
                  render={({field}) => (
                    <Autocomplete
                      {...field}
                      multiple
                      id="companies"
                      limitTags={3}
                      options={companiesList || []}
                      loading={false}
                      size="small"
                      popupIcon={<SelectPopupIcon />}
                      PaperComponent={(props) => (
                        <Paper
                          sx={{
                            borderRadius: '12px',
                            boxShadow: 'var(--shadow-card)',
                            marginTop: '8px',
                            cursor: 'pointer',
                          }}
                          {...props}
                        />
                      )}
                      ListboxProps={{
                        style: {maxHeight: '300px'},
                        className: 'autoCompleteListBox',
                      }}
                      isOptionEqualToValue={(option, value) =>
                        value.name === '' ? true : option.id === value.id
                      }
                      getOptionLabel={(option) =>
                        option?.name ? option.name : ''
                      }
                      onChange={(_, value) => {
                        field.onChange(value);
                      }}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            {...getTagProps({index})}
                            size="small"
                            label={option.name}
                            sx={chipStyles}
                            deleteIcon={
                              <CloseIcon
                                style={{width: '24px', height: '24px'}}
                              />
                            }
                          />
                        ))
                      }
                      renderOption={(props: any, option) => (
                        <span {...props} className={styles.dropDownItem}>
                          {option.name}
                        </span>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Companies"
                          sx={searchAndSelect}
                          error={!!errors.companies}
                        />
                      )}
                    />
                  )}
                />
                <ErrorMessage
                  name="companies"
                  errors={errors}
                  render={({message}) => (
                    <span className={styles.inputErrorMessage}>{message}</span>
                  )}
                />
              </div>

              <div className={styles.additionalInfoInputContainer}>
                <label htmlFor="additionalInformation">
                  Additional information - Optional
                </label>
                <input
                  {...register('additionalInformation')}
                  type="text"
                  id="additionalInformation"
                  autoComplete="off"
                  className={`input`}
                  placeholder="Enter additional information"
                />
              </div>
              <Divider
                sx={{backgroundColor: 'var(--border)', margin: '28px 0 14px 0'}}
              />
              <div className={styles.buttonsContainer}>
                <Button sx={cancelButton} onClick={clearDataAndErrors}>
                  Cancel
                </Button>
                <ButtonWithProgress
                  type="submit"
                  disabled={isCreatingCompanyOwnerLoading}
                  loading={isCreatingCompanyOwnerLoading}
                  sx={createButtonModal}
                >
                  Create
                </ButtonWithProgress>
              </div>
            </Box>
          </form>
        </Modal>
      </Backdrop>
    </div>
  );
};

export default CompanyOwnersCreate;
